import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import { RFQProvider } from "./components/RFQProvider";
import { Toaster } from "sonner";

import PublicLayout from "./components/PublicLayout";
import HomePage from "./pages/Home";
import AboutPage from "./pages/About";
import ProductsPage from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import NewsPage from "./pages/News";
import NewsDetail from "./pages/NewsDetail";
import ContactPage from "./pages/Contact";
import LoginPage from "./pages/Login";

import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/Dashboard";
import ProductsAdmin from "./pages/admin/ProductsAdmin";
import NewsAdmin from "./pages/admin/NewsAdmin";
import QuotationsAdmin from "./pages/admin/QuotationsAdmin";
import UsersAdmin from "./pages/admin/UsersAdmin";
import ServiceReportsAdmin from "./pages/admin/ServiceReportsAdmin";
import InstallationReportsAdmin from "./pages/admin/InstallationReportsAdmin";
import VehicleReportsAdmin from "./pages/admin/VehicleReportsAdmin";
import EstimatedQuotationsAdmin from "./pages/admin/EstimatedQuotationsAdmin";
import ProformaInvoicesAdmin from "./pages/admin/ProformaInvoicesAdmin";
import PurchaseOrdersAdmin from "./pages/admin/PurchaseOrdersAdmin";
import DispatchStickersAdmin from "./pages/admin/DispatchStickersAdmin";
import ExpensesPage from "./pages/admin/ExpensesPage";
import ExpensesAdminPage from "./pages/admin/ExpensesAdminPage";
import SessionsAdminPage from "./pages/admin/SessionsAdminPage";
import UnitsAdmin from "./pages/admin/UnitsAdmin";
import UnitPicker from "./components/UnitPicker";
import AdminOnly from "./components/AdminOnly";

// Public Privacy Policy page (defined inline so no extra file is needed).
function PrivacyPolicyPage() {
  return (
    <div data-testid="privacy-policy-page" className="bg-white">
      <section className="bg-slate-950 text-white">
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-20">
          <div className="text-amber-400 font-mono text-xs tracking-widest font-bold mb-3">LEGAL</div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Privacy Policy for UEE India</h1>
          <p className="mt-3 text-slate-300">Last updated: June 2026</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16 text-slate-700 leading-relaxed space-y-8">
        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Information We Collect</h2>
          <p>
            UEE India collects login information such as email and account details, and the
            data users enter in the app, including service reports, installation reports, and
            expense details.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">How We Use Information</h2>
          <p>
            This information is used only to operate the app, manage user accounts, save user
            reports and expenses, and provide support.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Data Sharing &amp; Protection</h2>
          <p>
            We do not sell user data and do not share it with third parties except as required
            by law. We take reasonable steps to protect user data from unauthorized access.
          </p>
        </div>

        <div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Your Rights</h2>
          <p>Users can request access to or deletion of their data by contacting us.</p>
        </div>

        <div className="border-t border-slate-200 pt-8">
          <h2 className="text-xl font-bold text-slate-900 mb-2">Contact Us</h2>
          <p>
            For any questions about this Privacy Policy, contact us at:{" "}
            <a href="mailto:info@ueeindia.com" className="text-amber-600 font-semibold underline">
              info@ueeindia.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RFQProvider>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/products" element={<ProductsPage />} />
                <Route path="/products/:id" element={<ProductDetail />} />
                <Route path="/news" element={<NewsPage />} />
                <Route path="/news/:id" element={<NewsDetail />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              </Route>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route path="products" element={<AdminOnly><ProductsAdmin /></AdminOnly>} />
                <Route path="news" element={<AdminOnly><NewsAdmin /></AdminOnly>} />
                <Route path="quotations" element={<AdminOnly><QuotationsAdmin /></AdminOnly>} />
                <Route path="users" element={<AdminOnly><UsersAdmin /></AdminOnly>} />
                <Route path="expenses" element={<ExpensesPage />} />
                <Route path="expenses-summary" element={<AdminOnly><ExpensesAdminPage /></AdminOnly>} />
                <Route path="sessions" element={<AdminOnly><SessionsAdminPage /></AdminOnly>} />
                <Route path="service-reports" element={<ServiceReportsAdmin />} />
                <Route path="installation-reports" element={<InstallationReportsAdmin />} />
                <Route path="inspection-reports" element={<VehicleReportsAdmin />} />
                <Route path="vehicle-reports" element={<VehicleReportsAdmin />} />
                <Route path="estimated-quotations" element={<AdminOnly><EstimatedQuotationsAdmin /></AdminOnly>} />
                <Route path="proforma-invoices" element={<AdminOnly><ProformaInvoicesAdmin /></AdminOnly>} />
                <Route path="purchase-orders" element={<AdminOnly><PurchaseOrdersAdmin /></AdminOnly>} />
                <Route path="dispatch-stickers" element={<AdminOnly><DispatchStickersAdmin /></AdminOnly>} />
                <Route path="units" element={<AdminOnly><UnitsAdmin /></AdminOnly>} />
              </Route>
            </Routes>
            <UnitPicker />
          </RFQProvider>
        </AuthProvider>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </div>
  );
}

export default App;
